import { ref, readonly } from 'vue'

export interface CookiePreferences {
  essential: boolean
  analytics: boolean
  marketing: boolean
}

const STORAGE_KEY = 'couqoo_cookie_consent'

const defaultPreferences: CookiePreferences = {
  essential: true,
  analytics: false,
  marketing: false
}

function loadPreferences(): CookiePreferences | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return null
    return JSON.parse(stored) as CookiePreferences
  } catch {
    return null
  }
}

function savePreferences(prefs: CookiePreferences) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs))
}

const stored = loadPreferences()
const hasConsented = ref(stored !== null)
const preferences = ref<CookiePreferences>(stored || { ...defaultPreferences })

export function useCookieConsent() {
  function acceptAll() {
    preferences.value = { essential: true, analytics: true, marketing: true }
    savePreferences(preferences.value)
    hasConsented.value = true
  }

  function rejectNonEssential() {
    preferences.value = { essential: true, analytics: false, marketing: false }
    savePreferences(preferences.value)
    hasConsented.value = true
  }

  function saveCustom(prefs: Partial<CookiePreferences>) {
    preferences.value = { ...preferences.value, ...prefs, essential: true }
    savePreferences(preferences.value)
    hasConsented.value = true
  }

  function resetConsent() {
    localStorage.removeItem(STORAGE_KEY)
    hasConsented.value = false
    preferences.value = { ...defaultPreferences }
  }

  return {
    hasConsented: readonly(hasConsented),
    preferences: readonly(preferences),
    acceptAll,
    rejectNonEssential,
    saveCustom,
    resetConsent
  }
}
